import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, FileText, Cookie, Scale, Mail, Network, FileSignature, Eye, AlertCircle, Lock, BadgeCheck } from "lucide-react";
import { PageHeader, CTABand } from "./Services";

const POLICY_CARDS = [
  { icon: Eye, title: "Privacy Policy", desc: "How personal information is collected, used and protected.", to: "/legal/privacy-policy" },
  { icon: Cookie, title: "Cookie Policy", desc: "How and why cookies and similar technologies are used.", to: "/legal/cookie-policy" },
  { icon: Scale, title: "Website Terms and Conditions", desc: "Terms governing access and use of PlayerReach.io.", to: "/legal/terms" },
  { icon: Mail, title: "SMS and Email Marketing Policy", desc: "Standards for the planning and delivery of marketing.", to: "/legal/sms-email-marketing-policy" },
  { icon: Network, title: "Acceptable Traffic Sources Policy", desc: "Standards for partner traffic and ongoing review.", to: "/legal/acceptable-traffic-sources-policy" },
  { icon: FileSignature, title: "Data Usage Agreement", desc: "Contractual standards for lawful data exchange.", to: "/legal/data-usage-agreement" },
];

const PRINCIPLES = [
  { icon: ShieldCheck, t: "Lawful data sources", d: "Owned, licensed or partner data backed by documented rights." },
  { icon: BadgeCheck, t: "Clear sender identification", d: "Recognisable sender on every direct marketing communication." },
  { icon: AlertCircle, t: "Opt-out and suppression controls", d: "Centralised suppression applied before deployment." },
  { icon: FileText, t: "Partner due diligence", d: "Traffic sources, consent records and history reviewed." },
  { icon: Eye, t: "Traffic quality monitoring", d: "Active review of complaint, opt-out and conversion signals." },
  { icon: Mail, t: "Complaint review", d: "Recipient and platform complaints triaged and acted on." },
  { icon: Lock, t: "Security and record keeping", d: "Access controls, segregation and documented retention." },
  { icon: Scale, t: "Regulated sector awareness", d: "Sector rules considered in creative, audience and channel." },
];

export default function Compliance() {
  React.useEffect(() => {
    document.title = "Compliance Hub | PlayerReach";
  }, []);
  return (
    <div data-testid="compliance-page">
      <PageHeader
        eyebrow="Compliance"
        title="Built for accountable acquisition."
        subtitle="PlayerReach supports commercial growth through documented standards for data usage, traffic sources, email marketing, SMS marketing, privacy, cookies and website operations."
      />

      {/* Policy cards */}
      <section className="section-pad">
        <div className="container-pr">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <p className="overline">Operating policies</p>
              <h2 className="font-display mt-3 text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">Six policies. One operating model.</h2>
            </div>
            <Link to="/legal" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-[#0052FF] hover:underline" data-testid="compliance-legal-link">
              Legal Centre <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {POLICY_CARDS.map((p, i) => (
              <Link key={p.title} to={p.to} className="group relative card-flat p-7 transition-transform hover:-translate-y-0.5" data-testid={`compliance-policy-${i}`}>
                <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-[#0052FF]" />
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-slate-50 border border-slate-200 text-[#0052FF]">
                    <p.icon className="h-5 w-5" />
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-[#0052FF] transition-colors" />
                </div>
                <h3 className="font-display mt-5 text-lg font-semibold text-slate-900">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#0052FF]">Read policy <ArrowUpRight className="h-3.5 w-3.5" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Governance principles */}
      <section className="bg-slate-50 border-y border-slate-200 section-pad">
        <div className="container-pr">
          <p className="overline">Governance Principles</p>
          <h2 className="font-display mt-3 text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight max-w-2xl">The standards we hold ourselves and our partners to.</h2>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PRINCIPLES.map((p, i) => (
              <div key={p.t} className="card-flat p-6" data-testid={`principle-${i}`}>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-cyan-50 border border-cyan-200 text-cyan-700">
                  <p.icon className="h-5 w-5" />
                </span>
                <p className="font-display mt-4 font-semibold text-slate-900">{p.t}</p>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
